import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Modal,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CallEndIcon from '@mui/icons-material/CallEnd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { grey } from '@mui/material/colors';
import { keyframes } from '@emotion/react';
import { alpha, lighten } from '@mui/material/styles';

import { PRIMARYCOLOR } from '../../Constants';
import { formatAttemptPolicy } from '../pentests/vishing/constants';

const PASS_GREEN = '#2E7D32';
const FAIL_RED = '#C62828';
const SLATE_TEXT = '#607086';

const toBool = (value) => (typeof value === 'string' ? value.toLowerCase() === 'true' : !!value);

const COUNTRY_PREFIXES = [
  { code: '90', name: 'Turkey', flag: 'TR' },
  { code: '1', name: 'USA/Canada', flag: 'US' },
  { code: '44', name: 'United Kingdom', flag: 'GB' },
  { code: '33', name: 'France', flag: 'FR' },
  { code: '49', name: 'Germany', flag: 'DE' },
  { code: '34', name: 'Spain', flag: 'ES' },
  { code: '39', name: 'Italy', flag: 'IT' },
  { code: '91', name: 'India', flag: 'IN' },
  { code: '81', name: 'Japan', flag: 'JP' },
  { code: '86', name: 'China', flag: 'CN' }
];

const getCountryFromPhone = (phone) => {
  if (!phone) return { flag: 'ZZ', name: 'Unknown' };
  const digits = phone.replace(/[^\d]/g, '');
  const number = digits.startsWith('00') ? digits.slice(2) : digits;
  let match = null;

  for (const entry of COUNTRY_PREFIXES.sort((a, b) => b.code.length - a.code.length)) {
    if (number.startsWith(entry.code)) {
      match = entry;
      break;
    }
  }

  return match || { flag: 'ZZ', name: 'Unknown' };
};

const statusColor = (status) => {
  if (!status) return 'default';
  const value = status.toLowerCase();
  if (value.includes('in') || value.includes('ring') || value.includes('ongo')) return 'info';
  if (value.includes('answer') || value.includes('miss')) return 'warning';
  if (value.includes('fail') || value.includes('error')) return 'error';
  return 'success';
};

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.12); opacity: 0.85; }
  100% { transform: scale(1); opacity: 1; }
`;

const bars = keyframes`
  0% { height: 18%; }
  50% { height: 90%; }
  100% { height: 18%; }
`;

const PhonePreview = ({ phone, name, isPlaying, onToggle }) => (
  <Box
    sx={{
      width: 228,
      height: 460,
      borderRadius: '34px',
      bgcolor: '#0C0F17',
      color: '#ffffff',
      position: 'relative',
      boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
      overflow: 'hidden',
      mx: 'auto',
      cursor: 'pointer',
      border: '1px solid rgba(255,255,255,0.06)'
    }}
    onClick={onToggle}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 96,
        height: 16,
        bgcolor: '#111111',
        borderRadius: '12px',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)'
      }}
    />

    <Box
      sx={{
        height: 28,
        background: 'linear-gradient(180deg,#0B0F1A,#0A0D14)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        letterSpacing: 0.5,
        opacity: 0.8
      }}
    >
      9:30
    </Box>

    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: 150,
        p: 1,
        background: `radial-gradient(80% 60% at 50% 0%, ${alpha(PRIMARYCOLOR, 0.2)}, transparent)`
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        {phone || 'Unknown'}
      </Typography>
      {name ? (
        <Typography variant="caption" sx={{ opacity: 0.72 }}>
          {name}
        </Typography>
      ) : null}
      <Box sx={{ width: 84, height: 8, borderRadius: 4, backgroundColor: '#2A3550', mt: 2 }} />
    </Box>

    <Box sx={{ position: 'absolute', left: 0, right: 0, top: '46%', display: 'flex', justifyContent: 'center' }}>
      <Avatar
        sx={{
          width: 66,
          height: 66,
          bgcolor: PRIMARYCOLOR,
          boxShadow: `0 6px 18px ${alpha(PRIMARYCOLOR, 0.4)}`,
          animation: isPlaying ? 'none' : `${pulse} 1.2s ease-in-out infinite`
        }}
      >
        {isPlaying ? <PauseIcon fontSize="large" /> : <PlayArrowIcon fontSize="large" />}
      </Avatar>
    </Box>

    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: '62%',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: 0.75,
        height: 38
      }}
    >
      {[0, 0.15, 0.3, 0.45, 0.6].map((delay, index) => (
        <Box
          key={index}
          sx={{
            width: 6,
            height: isPlaying ? 24 : 12,
            borderRadius: 1,
            background: 'linear-gradient(180deg,#7AB7FF,#2C6FB7)',
            opacity: 0.9,
            animation: isPlaying ? `${bars} 1.2s ${delay}s ease-in-out infinite` : 'none'
          }}
        />
      ))}
    </Box>

    <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 24, display: 'flex', justifyContent: 'center' }}>
      <Avatar sx={{ width: 44, height: 44, bgcolor: FAIL_RED, boxShadow: `0 6px 18px ${alpha(FAIL_RED, 0.4)}` }}>
        <CallEndIcon />
      </Avatar>
    </Box>

    <Box
      sx={{
        pointerEvents: 'none',
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.06), transparent 30%, transparent 70%, rgba(255,255,255,0.04))'
      }}
    />
  </Box>
);

const TranscriptBubble = ({ role, text }) => {
  const isUser = role === 'user';
  const isAi = role === 'ai';

  return (
    <ListItem disableGutters sx={{ justifyContent: isUser ? 'flex-end' : 'flex-start', pb: 0.7 }}>
      <Paper
        elevation={0}
        sx={{
          px: 1.5,
          py: 1.1,
          maxWidth: '78%',
          bgcolor: isUser ? lighten(PRIMARYCOLOR, 0.12) : grey[100],
          color: isUser ? '#ffffff' : '#1E293B',
          borderRadius: 2,
          border: `1px solid ${isUser ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.06)'}`
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 800, opacity: isUser ? 0.92 : 0.72 }}>
          {isUser ? 'User' : isAi ? 'AI' : 'System'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.35, whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
          {text}
        </Typography>
      </Paper>
    </ListItem>
  );
};

const StaticTranscript = ({ transcript }) => {
  const messages = useMemo(() => {
    if (!transcript) return [];
    return transcript
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const match = line.match(/^(AI|User)\s*:\s*(.*)$/i);
        if (!match) return { role: 'system', text: line };
        return { role: match[1].toLowerCase(), text: match[2] };
      });
  }, [transcript]);

  if (!messages.length) {
    return (
      <Typography variant="body2" sx={{ color: SLATE_TEXT }}>
        No transcript available for this call.
      </Typography>
    );
  }

  return (
    <List
      sx={{
        maxHeight: 420,
        overflowY: 'auto',
        pr: 1,
        '&::-webkit-scrollbar': { width: 8 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(PRIMARYCOLOR, 0.18),
          borderRadius: 999
        }
      }}
    >
      {messages.map((message, index) => (
        <TranscriptBubble key={index} role={message.role} text={message.text} />
      ))}
    </List>
  );
};

export const VishingCallModal = ({ open, onClose, call }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const transcriptCount = useMemo(() => {
    if (!call?.transcript) return 0;
    return call.transcript.split('\n').map((line) => line.trim()).filter(Boolean).length;
  }, [call?.transcript]);

  const formattedResults = useMemo(() => {
    if (!call?.results || call.results === '{}') return null;
    try {
      return JSON.stringify(JSON.parse(call.results), null, 2);
    } catch {
      return call.results;
    }
  }, [call?.results]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying((current) => !current);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onEnded = () => setPlaying(false);
    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, []);

  useEffect(() => {
    if (!open && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    }
  }, [open]);

  if (!call) return null;

  const failed = toBool(call?.passOrFail);
  const country = getCountryFromPhone(call?.phone);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '92%',
          maxWidth: 1024,
          maxHeight: '92vh',
          overflowY: 'auto',
          bgcolor: '#F7FAFD',
          borderRadius: 4,
          border: '1px solid',
          borderColor: alpha(PRIMARYCOLOR, 0.12),
          boxShadow: `0 28px 60px ${alpha('#0F172A', 0.22)}`,
          p: { xs: 2, md: 3 },
          outline: 'none'
        }}
      >
        <audio ref={audioRef} src={call?.recording || ''} preload="auto" style={{ display: 'none' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
              Call Review
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 800, color: '#10233B' }}>
              Call with {call.name || call.phone || 'Unknown'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.7, color: SLATE_TEXT, lineHeight: 1.75 }}>
              Review the audio, transcript, summary, and any captured data associated with this vishing interaction.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
            <Chip
              size="small"
              label={(call?.status || '—').toUpperCase()}
              color={statusColor(call?.status)}
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              size="small"
              label={failed ? 'FAILED' : 'PASSED'}
              sx={{
                fontWeight: 900,
                bgcolor: failed ? alpha(FAIL_RED, 0.1) : alpha(PASS_GREEN, 0.1),
                color: failed ? FAIL_RED : PASS_GREEN
              }}
            />
            {call?.recording ? (
              <Chip size="small" label="Recording Available" sx={{ fontWeight: 800, bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }} />
            ) : null}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <PhonePreview phone={call.phone} name={call.name} isPlaying={playing} onToggle={togglePlay} />

            <Card
              variant="outlined"
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 3,
                borderColor: alpha(PRIMARYCOLOR, 0.12),
                boxShadow: `0 10px 24px ${alpha(PRIMARYCOLOR, 0.05)}`
              }}
            >
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PRIMARYCOLOR }}>
                Call Context
              </Typography>

              <Stack spacing={1.15} sx={{ pt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ width: 76, color: SLATE_TEXT, fontWeight: 700 }}>
                    Phone
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#20344B' }}>
                    {call.phone || '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', color: SLATE_TEXT }}>
                    <span style={{ marginRight: 6 }}>{country.flag === 'ZZ' ? 'GL' : country.flag}</span>
                    {country.name}
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ width: 76, color: SLATE_TEXT, fontWeight: 700 }}>
                    Scenario
                  </Typography>
                  <Avatar src={call?.scenarioInfo?.image} alt={call?.scenarioInfo?.name} sx={{ width: 28, height: 28 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#20344B' }}>
                    {call?.scenarioInfo?.name || (call?.scenario ? `#${call.scenario}` : '—')}
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ width: 76, color: SLATE_TEXT, fontWeight: 700 }}>
                    Voice
                  </Typography>
                  <Avatar src={call?.voiceInfo?.image} alt={call?.voiceInfo?.name} sx={{ width: 28, height: 28 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#20344B' }}>
                    {call?.voiceInfo?.name || (call?.voice ? `#${call.voice}` : '—')}
                  </Typography>
                  {call?.voiceInfo?.language ? (
                    <Chip size="small" label={call.voiceInfo.language.toUpperCase()} sx={{ fontWeight: 700 }} />
                  ) : null}
                </Stack>

                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Typography variant="caption" sx={{ width: 76, flexShrink: 0, color: SLATE_TEXT, fontWeight: 700 }}>
                    Caller ID
                  </Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#20344B', wordBreak: 'break-word' }}>
                      {call.caller_number || 'Automatic round robin'}
                    </Typography>
                    {call.last_caller_number ? (
                      <Typography variant="caption" sx={{ color: SLATE_TEXT }}>
                        Last used: {call.last_caller_number}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>

                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Typography variant="caption" sx={{ width: 76, flexShrink: 0, color: SLATE_TEXT, fontWeight: 700 }}>
                    Redials
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#20344B' }}>
                    {formatAttemptPolicy(call.max_redials)}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ pt: 1.15 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={togglePlay}
                    disabled={!call?.recording}
                    startIcon={playing ? <PauseIcon /> : <PlayArrowIcon />}
                    sx={{ fontWeight: 700 }}
                  >
                    {playing ? 'Pause' : 'Play'}
                  </Button>
                  {call?.recording ? (
                    <Button
                      component="a"
                      href={call.recording}
                      target="_blank"
                      rel="noreferrer"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      sx={{ minWidth: 120, fontWeight: 700 }}
                    >
                      Audio
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Card>

            {call?.person_bio ? (
              <Card variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 3, borderColor: alpha(PRIMARYCOLOR, 0.12) }}>
                <Typography variant="subtitle2" sx={{ mb: 0.6, fontWeight: 800, color: '#10233B' }}>
                  Person Bio
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: SLATE_TEXT, lineHeight: 1.75 }}>
                  {call.person_bio}
                </Typography>
              </Card>
            ) : null}
          </Grid>

          <Grid item xs={12} md={8}>
            {call?.summary ? (
              <Card
                variant="outlined"
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 3,
                  borderColor: alpha(PRIMARYCOLOR, 0.12),
                  borderLeft: `4px solid ${PRIMARYCOLOR}`,
                  backgroundColor: '#ffffff'
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.6, fontWeight: 800, color: '#10233B' }}>
                  Summary
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: SLATE_TEXT, lineHeight: 1.8 }}>
                  {call.summary}
                </Typography>
              </Card>
            ) : null}

            <Card variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: alpha(PRIMARYCOLOR, 0.12), backgroundColor: '#ffffff' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#10233B' }}>
                    Transcript
                  </Typography>
                  <Chip size="small" label={`${transcriptCount} entries`} sx={{ fontWeight: 700, bgcolor: alpha(PRIMARYCOLOR, 0.08), color: PRIMARYCOLOR }} />
                </Stack>
                <Tooltip title="Copy transcript">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(call?.transcript || '');
                      } catch (_) {}
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Divider sx={{ mb: 1.5 }} />
              <StaticTranscript transcript={call?.transcript} />
            </Card>

            {formattedResults ? (
              <Card variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 3, borderColor: alpha(PRIMARYCOLOR, 0.12), backgroundColor: '#ffffff' }}>
                <Typography variant="subtitle2" sx={{ mb: 0.7, fontWeight: 800, color: '#10233B' }}>
                  Captured Data
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: 2,
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    bgcolor: '#0B1220',
                    color: '#D8E2F1',
                    border: `1px solid ${alpha(PRIMARYCOLOR, 0.35)}`
                  }}
                >
                  {formattedResults}
                </Box>
              </Card>
            ) : null}
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, textAlign: 'right' }}>
          <Button variant="contained" onClick={onClose} sx={{ fontWeight: 700 }}>
            Done
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default VishingCallModal;
